import { render } from "https://esm.sh/preact@10.23.1";
import { useState, useEffect, useCallback, useRef } from "https://esm.sh/preact@10.23.1/hooks";
import { html } from "https://esm.sh/htm@3.1.1/preact?deps=preact@10.23.1";

/**
 * Custom hook: useFacetAccordion
 *
 * Manages expand/collapse state for Algolia native facet panels.
 * Each panel is identified by its dropdown-checkbox id.
 *
 * @param {Object} options
 * @param {string} options.containerSelector - CSS selector for the facets container
 * @param {boolean} options.startOpen - Whether panels start expanded (default: true)
 * @returns {{ toggle, isOpen, openAll, closeAll }}
 */
function useFacetAccordion({ containerSelector = ".ais-facets", startOpen = true } = {}) {
  const [openPanels, setOpenPanels] = useState({});

  const initializePanels = useCallback(() => {
    const container = document.querySelector(containerSelector);
    if (!container) return;

    const checkboxes = container.querySelectorAll(".ais-dropdown-checkbox");
    if (checkboxes.length === 0) return;

    setOpenPanels((prev) => {
      const next = { ...prev };
      let changed = false;
      checkboxes.forEach((cb) => {
        if (!(cb.id in next)) {
          next[cb.id] = startOpen;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [containerSelector, startOpen]);

  const toggle = useCallback((panelId) => {
    setOpenPanels((prev) => ({ ...prev, [panelId]: !(prev[panelId] ?? startOpen) }));
  }, [startOpen]);

  const isOpen = useCallback(
    (panelId) => openPanels[panelId] ?? startOpen,
    [openPanels, startOpen]
  );

  const openAll = useCallback(() => {
    setOpenPanels((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => (next[k] = true));
      return next;
    });
  }, []);

  const closeAll = useCallback(() => {
    setOpenPanels((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => (next[k] = false));
      return next;
    });
  }, []);

  return { toggle, isOpen, openAll, closeAll, initializePanels };
}

/**
 * Checks whether Algolia has finished rendering facet content.
 * Returns true when at least one RefinementList has list items.
 */
function hasFacetContent(container) {
  const items = container.querySelectorAll(".ais-RefinementList-item");
  return items.length > 0;
}

// --- Component that binds accordion behavior to existing DOM ---
function FacetAccordionController() {
  const { toggle, isOpen, initializePanels } = useFacetAccordion({ startOpen: true });
  const bound = useRef(false);
  const revealed = useRef(false);

  useEffect(() => {
    function bindAndReveal() {
      const container = document.querySelector(".ais-facets");
      if (!container) return false;

      // Wait until Algolia has actually rendered facet content
      if (!hasFacetContent(container)) return false;

      const wrappers = container.querySelectorAll(".ais-facet-dropdown-wrapper");
      if (wrappers.length === 0) return false;

      wrappers.forEach((wrapper) => {
        const checkbox = wrapper.querySelector(".ais-dropdown-checkbox");
        if (!checkbox || wrapper.dataset.accordionBound) return;

        wrapper.dataset.accordionBound = "true";

        wrapper.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          toggle(checkbox.id);
        });
      });

      // Initialize panel open/closed state now that DOM is ready
      initializePanels();

      // Reveal facets now that content is ready
      if (!revealed.current) {
        container.classList.add("is-ready");
        revealed.current = true;
      }

      return true;
    }

    // Poll until Algolia renders the facets with content
    if (!bound.current) {
      const interval = setInterval(() => {
        if (bindAndReveal()) {
          bound.current = true;
          clearInterval(interval);
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, [toggle, initializePanels]);

  // Apply open/closed state to DOM
  useEffect(() => {
    const container = document.querySelector(".ais-facets");
    if (!container) return;

    const panels = container.querySelectorAll(".ais-DynamicWidgets-widget .ais-Panel, .ais-clear-refinements-container .ais-Panel, .ais-current-refined-values-container .ais-Panel");

    panels.forEach((panel) => {
      const checkbox = panel.querySelector(".ais-dropdown-checkbox");
      if (!checkbox) return;

      const body = panel.querySelector(".ais-Panel-body");
      const wrapper = panel.querySelector(".ais-facet-dropdown-wrapper");
      if (!body || !wrapper) return;

      const open = isOpen(checkbox.id);

      body.style.display = open ? "" : "none";
      wrapper.classList.toggle("is-open", open);

      // Sync the hidden checkbox for CSS chevron rotation
      checkbox.checked = open;
    });
  });

  return null;
}

// --- Mount when DOM is ready ---
function init() {
  const mount = document.createElement("div");
  mount.id = "algolia-facet-accordion-mount";
  mount.style.display = "none";
  document.body.appendChild(mount);
  render(html`<${FacetAccordionController} />`, mount);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
